<template>
  <Html>
    <Body>
      <div id="main" ref="mainContainer">
        <lazy-full-spinner ref="fullSpinner" />

        <lazy-action-header
          v-if="offer"
          :key="offer.key"
          :text="offer.text"
          :countdown-to="offer.deadline"
          :progress="offer.progress"
          :show-progress="offer.showProgress"
          @click.passive="notifyClick"
          @expired="expireCampaign"
        />

        <topbar />

        <main class="flex-1" @click.passive="notifyClick">
          <NuxtPage />
        </main>

        <button
          v-show="showScrollTop"
          :class="scrollTopClasses"
          type="button"
          @click="scrollTop"
        >
          <s-icon icon="chevron-up" dimension="25" />
        </button>

        <lazy-notification-bar />
        <lazy-load-progress />
        <lazy-app-footer @click.passive="notifyClick" />

        <ClientOnly>
          <lazy-cookie-disclaimer
            v-if="showCookieDisclaimer"
            :initial-analytics="analyticsAllowed"
            :initial-marketing="marketingAllowed"
            @agreed="acceptCookies"
            @declined="declineCookies"
            @saved="saveCookiePreferences"
          />
        </ClientOnly>
      </div>
    </Body>
  </Html>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, provide, ref, watch } from 'vue';
import tailwindConfig from '~/tailwind.config';
import { useMainStore } from '~/store';
import {
  siteCampaignOffer,
  siteSettingsLanguage,
  settingEnabled
} from '~/utils/site-settings';
const route = useRoute();
const nuxtApp = useNuxtApp();
const { t, locale, locales } = useI18n();
const localePath = useLocalePath();
const siteSettingsLocale = computed(() => siteSettingsLanguage(route.path));

/**
 * ✅ strictSeo ist aktiv:
 * - KEIN useLocaleHead() verwenden (sonst Fehler)
 * - lang/dir selbst setzen via useHead(htmlAttrs)
 */
const currentLocale = computed(() => {
  const list = locales.value as any[];
  return list?.find((l) => l.code === locale.value);
});

const htmlLang = computed(() => currentLocale.value?.language || locale.value);
const htmlDir = computed(() => currentLocale.value?.dir || undefined);

/** i18n route name suffix entfernen */
const stripI18nSuffix = (name: unknown) =>
  String(name || 'index').replace(/___[a-z]{2}(?:-[A-Z]{2})?$/i, '');

const toCamelCase = (str: string) =>
  str.replace(/-./g, (m) => m.charAt(1).toUpperCase());

/** stabiler route name */
const rawRouteName = computed(() => {
  void locale.value;
  return stripI18nSuffix(route.name);
});

/** Kandidaten für metaData.* keys inkl. Home-Fallbacks */
const pageKeyCandidates = computed(() => {
  void locale.value;

  const raw = String(rawRouteName.value || 'index');
  const camel = toCamelCase(raw);

  const isHome = route.path === localePath('/') || raw === 'index';

  const candidates: string[] = [];
  candidates.push(camel);
  if (raw && raw !== camel) candidates.push(raw);

  if (isHome) {
    for (const k of ['home', 'homepage', 'start', 'landing', 'index']) {
      if (!candidates.includes(k)) candidates.push(k);
    }
  }

  return [...new Set(candidates.filter(Boolean))];
});

function firstI18nMatch(keys: string[]) {
  for (const key of keys) {
    const val = t(key);
    if (val !== key) {
      const s = String(val).trim();
      if (s.length) return s;
    }
  }
  return '';
}

/** ✅ HTML aus Description entfernen (Blog liefert oft <p>...</p>) */
function stripHtml(input: string) {
  if (!input) return '';
  return input
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '')
    .replace(/<\/?[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Title: i18n > route.meta > Default */
const title = computed(() => {
  void locale.value;

  const titleKeys = pageKeyCandidates.value.map((k) => `metaData.${k}.title`);
  const i18nTitle = firstI18nMatch(titleKeys);
  if (i18nTitle) return i18nTitle;

  const metaTitle = route.meta?.title as unknown;
  if (typeof metaTitle === 'function') return String((metaTitle as any)(route));
  if (typeof metaTitle === 'string' && metaTitle.trim().length)
    return metaTitle;

  return 'Idmanify';
});

/**
 * Description:
 * - nur echte Seite / meta / default i18n
 * - sonst leer => Tag wird NICHT gesetzt
 * - zusätzlich: HTML strippen
 */
const description = computed(() => {
  void locale.value;

  const descKeys = pageKeyCandidates.value.map(
    (k) => `metaData.${k}.description`
  );
  const i18nDesc = firstI18nMatch(descKeys);
  if (i18nDesc) return stripHtml(i18nDesc);

  const metaDesc = route.meta?.description as unknown;
  if (typeof metaDesc === 'function') {
    const v = String((metaDesc as any)(route)).trim();
    if (v) return stripHtml(v);
  }
  if (typeof metaDesc === 'string' && metaDesc.trim().length)
    return stripHtml(metaDesc.trim());

  const fallbackKey = 'metaData.default.description';
  const fallback = t(fallbackKey);
  if (fallback !== fallbackKey && String(fallback).trim().length)
    return stripHtml(String(fallback).trim());

  return '';
});

const {
  analyticsAllowed,
  marketingAllowed,
  preferencesOpen: showCookieDisclaimer,
  record: consentRecord,
  save: saveConsent,
  syncFromCookie
} = usePrivacyConsent();
const adState = useCookie<boolean | undefined>('adState', {
  sameSite: true,
  maxAge: 60 * 60 * 24
});

/** JSON-LD */
const jsonLd = computed(() => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Idmanify',
  url: 'https://www.idmanify.com',
  logo: 'https://www.idmanify.com/images/icons/idmanify-logo-black.png',
  contactPoint: [
    {
      '@type': 'ContactPoint',
      email: 'info@idmanify.com',
      contactType: 'customer service',
      availableLanguage: [
        'English',
        'German',
        'Turkish',
        'Russian',
        'Arabic',
        'Spanish',
        'French',
        'Italian',
        'Portuguese',
        'Hindi',
        'Urdu',
        'Japanese',
        'Chinese'
      ]
    }
  ],
  sameAs: [
    'https://www.instagram.com/idmanify',
    'https://www.tiktok.com/@idmanify_official'
  ]
}));

/**
 * ✅ useHead: nur "eigene" Sachen + htmlAttrs.
 * ❗ KEIN canonical/alternate/hreflang: das macht nuxt-i18n strictSeo automatisch.
 * ❗ Unhead: inline script via textContent (nicht children), kein __dangerouslyDisableSanitizersByTagID nötig.
 */
useHead(() => {
  void route.fullPath;
  void locale.value;

  return {
    htmlAttrs: {
      lang: htmlLang.value,
      dir: htmlDir.value
    },

    title: title.value,

    meta: [
      {
        key: 'viewport',
        name: 'viewport',
        content: 'width=device-width, initial-scale=1, viewport-fit=cover'
      },
      {
        key: 'format-detection',
        name: 'format-detection',
        content: 'telephone=no'
      },
      ...(description.value
        ? [
            {
              key: 'description',
              name: 'description',
              content: description.value
            }
          ]
        : [])
    ],

    script: [
      {
        key: 'json-ld-org',
        type: 'application/ld+json',
        textContent: JSON.stringify(jsonLd.value)
      }
    ]
  };
});

useSeoMeta({
  description: () => description.value || undefined,
  ogTitle: () => title.value,
  ogDescription: () => description.value || undefined,
  robots: () => (route.meta.robots ? String(route.meta.robots) : undefined),
  ogType: 'website',
  twitterCard: 'summary_large_image',
  twitterTitle: () => title.value,
  twitterDescription: () => description.value || undefined
});

/** ----- Rest: Funktionalität wie zuvor ----- */
const mainContainer = ref<HTMLElement | null>(null);
const fullSpinner = ref<any>(null);

const showFullSpinner = () => fullSpinner.value?.show?.();
const hideFullSpinner = () => fullSpinner.value?.hide?.();
provide('defaultParentMethods', { showFullSpinner, hideFullSpinner });

/** Auth Cookie (SSR-safe) */
const loggedInCookie = useCookie<string | null>('idmanify_user');
useAuth().session.value = loggedInCookie.value ?? null;

function acceptCookies() {
  saveConsent({ analytics: true, marketing: true });
  adState.value = true;
  showAdOverlay.value = false;
}
function declineCookies() {
  saveConsent({ analytics: false, marketing: false });
  adState.value = false;
  showAdOverlay.value = false;
}
function saveCookiePreferences(choices: {
  analytics: boolean;
  marketing: boolean;
}) {
  saveConsent(choices);
  adState.value = choices.marketing;
  showAdOverlay.value = false;
}

/** Public settings: locale-tagged data, one cache/request owner. */
const siteSettingsResponse = await useSiteSettings(siteSettingsLocale);
const siteSettings = computed(() => {
  const entry = siteSettingsResponse.data.value;
  return entry?.locale === siteSettingsLocale.value ? entry.settings : null;
});
const expiredCampaignKey = ref<string | null>(null);
const offer = computed(() => {
  const campaign = siteCampaignOffer(
    siteSettings.value,
    siteSettingsLocale.value
  );
  return campaign?.key === expiredCampaignKey.value ? null : campaign;
});
const overlayContent = computed(() => {
  const data = siteSettings.value;
  return settingEnabled(data?.popup_activated) &&
    typeof data?.popup_url === 'string'
    ? data.popup_url || null
    : null;
});
watch(
  () => Boolean(offer.value),
  (active) => {
    nuxtApp.$notifier?.setActionHeaderActive?.(active);
  },
  { immediate: true }
);
const showAdOverlay = ref(false);
let adTimeout: ReturnType<typeof setTimeout> | undefined;
function hideAd() {
  showAdOverlay.value = false;
  if (marketingAllowed.value) adState.value = true;
}
function expireCampaign(deadline: number) {
  if (offer.value?.deadline === deadline)
    expiredCampaignKey.value = offer.value.key;
}

/** Scroll/Resize/Breakpoints */
const breakpoints = ref<Record<string, number>>({});
const showScrollTop = ref(false);

const scrollTopClasses = computed(() => {
  const classes = [
    'scroll-top-btn',
    'z-20',
    'fixed',
    'text-white',
    'p-1',
    'bg-shade2',
    'rounded-full',
    'right-4',
    'bottom-6',
    'mb-8',
    'opacity-80'
  ];
  if (route.path === localePath('/userpanel')) classes.push('mb-10', 'sm:mb-0');
  return classes.join(' ');
});

function notifyClick() {
  useMainStore().bodyClick();
}

let scrollHandler: (() => void) | null = null;
let resizeHandler: (() => void) | null = null;
let intervalId: ReturnType<typeof setInterval> | undefined;

function scrollTop() {
  if (!import.meta.client) return;

  if (intervalId) clearInterval(intervalId);
  intervalId = setInterval(() => {
    if (window.pageYOffset === 0) {
      if (intervalId) clearInterval(intervalId);
      return;
    }
    window.scrollTo(0, window.pageYOffset - Math.ceil(window.innerHeight / 4));
  }, 10);
}

function computeBreakpoints() {
  const bp: Record<string, number> = {};
  const screens = (tailwindConfig as any)?.theme?.screens || {};
  for (const key of Object.keys(screens)) {
    bp[key] = Number(String(screens[key]).replace('px', ''));
  }
  breakpoints.value = bp;
}

function resizeListener() {
  const el = mainContainer.value;
  if (!el) return;

  let current = { name: 'xs', width: 0 };
  for (const name of Object.keys(breakpoints.value)) {
    const w = breakpoints.value[name];
    if (el.clientWidth >= w && w > current.width) current = { name, width: w };
  }

  const store = useMainStore();
  if (store.currentBreakpoint !== current.name)
    store.setCurrentBreakpoint(current.name);
}

function scrollListener() {
  const baseName = stripI18nSuffix(route.name);
  showScrollTop.value = window.scrollY > 150 && baseName !== 'userpanel';
}

onMounted(() => {
  computeBreakpoints();
  resizeListener();

  scrollHandler = () => scrollListener();
  resizeHandler = () => resizeListener();

  window.addEventListener('scroll', scrollHandler, { passive: true });
  window.addEventListener('resize', resizeHandler, { passive: true });

  syncFromCookie();
  if (!consentRecord.value) showCookieDisclaimer.value = true;

  if (adState.value === undefined) {
    adTimeout = setTimeout(() => {
      showAdOverlay.value = true;
    }, 10000);
  }
});

onBeforeUnmount(() => {
  if (adTimeout) clearTimeout(adTimeout);
  if (intervalId) clearInterval(intervalId);

  if (import.meta.client) {
    if (scrollHandler) window.removeEventListener('scroll', scrollHandler);
    if (resizeHandler) window.removeEventListener('resize', resizeHandler);
  }
});

/** Optional wie vorher vorhanden */
const showSpaceDiv = computed(() => {
  if (offer.value) return false;

  const whiteRoutes = ['/userpanel'];
  let currentLanguage = (nuxtApp as any).$i18n?.locale ?? locale.value;
  const fallback = (nuxtApp as any).$i18n?.fallbackLocale;
  if (fallback && currentLanguage === fallback) currentLanguage = '';

  for (const r of whiteRoutes) {
    const checkLngRoute = '/' + currentLanguage + r;
    const trailingOrWithoutTrailing =
      checkLngRoute[checkLngRoute.length - 1] === '/'
        ? checkLngRoute.slice(0, -1)
        : checkLngRoute + '/';

    if (
      route.path !== r &&
      route.path !== checkLngRoute &&
      route.path !== trailingOrWithoutTrailing
    ) {
      return false;
    }
  }
  return true;
});

defineExpose({ showAdOverlay, overlayContent, offer, showSpaceDiv, hideAd });
</script>

<style scoped>
@reference "@/assets/css/tailwind.css";

#main {
  @apply flex flex-col min-h-screen overflow-x-hidden font-base;
}
</style>
